import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ScanLine, X } from "lucide-react";
import { toast } from "sonner";
import { CameraScanner, type OCRResult } from "@/components/CameraScanner";
import { resolveScanDestination } from "@/lib/scanNavigation";

interface WorkOrderScanSearchProps {
  value: string;
  onChange: (value: string) => void;
}

export function WorkOrderScanSearch({ value, onChange }: WorkOrderScanSearchProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [scannerOpen, setScannerOpen] = useState(false);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleCapture = async (_typeShieldUrl: string, ocrResult?: OCRResult) => {
    if (ocrResult?.modelNumber) {
      toast.success(`Model detected: ${ocrResult.modelNumber}`);
      const destination = await resolveScanDestination(ocrResult);
      if (destination) {
        navigate(destination.path);
      } else {
        onChange(ocrResult.modelNumber);
        navigate("/search", { state: { scannedTypeShieldUrl: _typeShieldUrl, scannedResult: ocrResult } });
      }
    } else {
      toast.warning("Could not read a model number from the type shield");
    }
  };

  return (
    <>
      <div className="sticky top-0 z-20 -mx-6 px-6 py-3 bg-background/95 backdrop-blur">
        <div
          className="flex items-center gap-3 bg-card rounded-xl px-4 py-2.5 border-[1.5px] border-primary/60"
          style={{ boxShadow: "0 0 0 4px hsl(var(--primary) / 0.15)" }}
        >
          <Search className="w-5 h-5 text-primary shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Search work orders by model, brand, serial or name"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
            aria-label="Search work orders"
          />
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="text-muted-foreground hover:text-foreground shrink-0"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setScannerOpen(true)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium shrink-0 hover:bg-primary/20 transition-colors"
            aria-label="Scan type shield"
            title="Scan type shield"
          >
            <ScanLine className="w-4 h-4" />
            Scan
          </button>
        </div>
      </div>

      <CameraScanner
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onCapture={handleCapture}
      />
    </>
  );
}
